import type { ReactNode } from "react";
import { Button } from "@ascleo/ui/components/ui/button";
import type { ReviewContact } from "@ascleo/frontdesk-contracts/patient-info/review";
import { ChevronDown, Mail, Phone, Stethoscope } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AdditionalContactRow, ContactValue, RevealButton } from "./ContactRow";
import type { CategoryView } from "./category-model";

const CATEGORY_ICON = {
  phone: Phone,
  email: Mail,
  hausarzt: Stethoscope,
} as const;

/**
 * The shell of one check sheet row: icon tile, category label, handling chip, actions, and body.
 *
 * The body is a child rather than a mode flag, so the resting scan and the inline editor are
 * genuinely different trees composed by the caller instead of one component branching on a boolean.
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <CategoryCard category="phone" tone="resting" chip={<HandlingChip variant="reception" />}>
 *   {body}
 * </CategoryCard>
 * ```
 */
export function CategoryCard({
  category,
  tone,
  chip,
  actions,
  children,
}: {
  category: "phone" | "email" | "hausarzt";
  tone: "resting" | "editing";
  chip?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const Icon = CATEGORY_ICON[category];

  return (
    <li
      className={`flex min-w-0 flex-col gap-4 rounded-3xl border bg-card p-5 transition-colors duration-150 md:flex-row md:items-start ${
        tone === "editing" ? "border-primary/60" : ""
      }`}
      data-testid={`patient-info-category-${category}`}
      data-tone={tone}
    >
      <div className="flex min-w-0 flex-1 items-start gap-4">
        <span
          className="grid size-12 shrink-0 place-items-center rounded-2xl bg-primary-subtle text-primary-ink"
          aria-hidden="true"
        >
          <Icon className="size-6" />
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <span className="text-lg font-semibold text-foreground">
            {t(`self-service.check-in.patient-info.review.category.${category}`)}
          </span>
          {children}
          {chip !== undefined && <span className="mt-1">{chip}</span>}
        </div>
      </div>

      {actions !== undefined && (
        <div className="flex shrink-0 flex-wrap items-center gap-3">{actions}</div>
      )}
    </li>
  );
}

/**
 * The resting body of a phone or email row: the primary value, its tag, and the expander.
 *
 * Additional contacts stay collapsed until asked for — the common case is a patient confirming one
 * number at a glance, not auditing every contact the practice holds.
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <ContactCategoryBody view={view} revealed={new Set()} expanded={false} onToggleExpand={() => {}} onToggleReveal={() => {}} />
 * ```
 */
export function ContactCategoryBody({
  view,
  revealed,
  expanded,
  onToggleExpand,
  onToggleReveal,
}: {
  view: Extract<CategoryView, { kind: "contacts" }>;
  revealed: ReadonlySet<string>;
  expanded: boolean;
  onToggleExpand: () => void;
  onToggleReveal: (contact: ReviewContact) => void;
}) {
  const { t } = useTranslation();

  if (view.primary === undefined) {
    return (
      <span
        className="text-lg text-foreground"
        data-testid="patient-info-category-empty"
      >
        {t("self-service.check-in.patient-info.review.category-empty")}
      </span>
    );
  }

  const primary = view.primary;
  const count = view.additional.length;

  return (
    <>
      <span className="flex flex-wrap items-center gap-2">
        <ContactValue contact={primary} revealed={revealed.has(primary.itemId)} />
        <span className="text-base text-foreground">
          {t(`self-service.check-in.patient-info.review.type.${primary.type}`)}
        </span>
      </span>

      {count > 0 && (
        <>
          <Button
            type="button"
            variant="outline"
            size="2xl"
            className="w-fit justify-start"
            onClick={onToggleExpand}
            aria-expanded={expanded}
            data-testid="patient-info-category-expand-button"
          >
            <ChevronDown
              className={`size-6 transition-transform duration-150 ${
                expanded ? "rotate-180" : ""
              }`}
              aria-hidden="true"
            />
            {t(
              expanded
                ? `self-service.check-in.patient-info.review.collapse-${view.category}`
                : `self-service.check-in.patient-info.review.expand-${view.category}`,
              { count },
            )}
          </Button>
          {expanded && (
            <ul className="flex flex-col" data-testid="patient-info-additional-list">
              {view.additional.map((contact) => (
                <AdditionalContactRow
                  key={contact.itemId}
                  contact={contact}
                  revealed={revealed.has(contact.itemId)}
                  onToggleReveal={() => onToggleReveal(contact)}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </>
  );
}

/**
 * The resting body of the Hausarzt row.
 *
 * Free text, so there is nothing to mask and no reveal control — the value either is on file or is
 * not.
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <HausarztCategoryBody value="Dr. med. Anna Hoffmann" />
 * ```
 */
export function HausarztCategoryBody({ value }: { value: string | null }) {
  const { t } = useTranslation();
  return value === null || value.trim() === "" ? (
    <span
      className="text-lg text-foreground"
      data-testid="patient-info-category-empty"
    >
      {t("self-service.check-in.patient-info.review.category-empty")}
    </span>
  ) : (
    <span className="text-xl leading-tight break-words" data-testid="patient-info-hausarzt-value">
      {value}
    </span>
  );
}

export { RevealButton };
